<div class="parking-space-details bg-slate-50 min-h-[calc(100vh-64px)] p-4 sm:p-6">
    <div class="max-w-screen-xl mx-auto">

        <!-- ===== 1. Top bar & header ===== -->
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4 mb-5">
            <div class="flex items-center gap-3 min-w-0">
                <button type="button" (click)="goBack()"
                    class="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors shrink-0"
                    [attr.aria-label]="'spaceDetails.backAria' | translate">
                    <mat-icon class="!w-5 !h-5 !text-[20px] leading-5">chevron_left</mat-icon>
                    {{ 'spaceDetails.back' | translate }}
                </button>
                <div class="h-5 w-px bg-slate-200 shrink-0"></div>
                <div class="flex items-center gap-3 min-w-0">
                    <h1 class="text-xl font-bold text-slate-900 truncate">
                        {{ parkingSpace()?.name || ('spaceDetails.titleFallback' | translate) }}
                    </h1>
                    @if (parkingSpace()) {
                    @if (parkingSpace()?.isValid === false) {
                    <span class="status-pill status-pill--warn">
                        <span class="status-dot"></span> {{ 'spaceDetails.needsAttention' | translate }}
                    </span>
                    } @else {
                    <span class="status-pill status-pill--ok">
                        <span class="status-dot is-active"></span> {{ 'common.active' | translate }}
                    </span>
                    }
                    }
                </div>
            </div>

            @if (isAdmin()) {
            <div class="flex items-center gap-2 shrink-0">
                <button type="button" (click)="editParkingSpace()"
                    class="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors px-4 py-2 rounded-lg">
                    <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">edit</mat-icon>
                    {{ 'spaceDetails.editSpace' | translate }}
                </button>
            </div>
            }
        </div>

        @if (isLoading()) {
        <div class="flex items-center justify-center py-16">
            <mat-progress-spinner mode="indeterminate" diameter="46"></mat-progress-spinner>
        </div>
        } @else if (parkingSpace()) {

        <div class="space-y-4">

            <!-- ===== 2. Basic + Location ===== -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

                <!-- Card A: Basic Information (span 2) -->
                <section class="lg:col-span-2 detail-card bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                    <div class="flex items-center gap-2 mb-4">
                        <mat-icon class="section-icon !w-5 !h-5 !text-[20px] leading-5">info</mat-icon>
                        <h2 class="text-sm font-semibold text-slate-900">{{ 'spaceDetails.basicInfo' | translate }}</h2>
                    </div>

                    <div class="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4 text-sm">
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.type' | translate }}</div>
                            <span class="attr-pill attr-pill--blue">{{ parkingSpace()?.type || 'N/A' }}</span>
                        </div>
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.accessMode' | translate }}</div>
                            <span class="attr-pill attr-pill--green">{{ parkingSpace()?.accessMode || 'N/A' }}</span>
                        </div>
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.entryType' | translate }}</div>
                            <span class="attr-pill attr-pill--amber">{{ parkingSpace()?.entryType || 'N/A' }}</span>
                        </div>
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.restrictedWindow' | translate }}</div>
                            @if (restrictedWindow(); as window) {
                            <span class="attr-pill attr-pill--amber">
                                <mat-icon
                                    class="!w-3.5 !h-3.5 !text-[14px] leading-4 align-middle mr-0.5">schedule</mat-icon>{{
                                window }}
                            </span>
                            <div class="text-xs text-slate-500 mt-1">
                                {{ restrictedWindowDays() ?? ('spaceDetails.restrictedWindowEveryDay' | translate) }}
                            </div>
                            } @else {
                            <div class="kv-value">{{ 'spaceDetails.restrictedWindowNone' | translate }}</div>
                            }
                        </div>
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.createdBy' | translate }}</div>
                            <div class="kv-value truncate">
                                {{ parkingSpace()?.createdBy?.firstName }} {{ parkingSpace()?.createdBy?.lastName }}
                            </div>
                        </div>
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.createdAt' | translate }}</div>
                            <div class="kv-value">{{ parkingSpace()?.createdAt | date:'mediumDate' }}</div>
                        </div>
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.lastUpdated' | translate }}</div>
                            <div class="kv-value">{{ parkingSpace()?.updatedAt | date:'mediumDate' }}</div>
                        </div>
                        <div>
                            <div class="kv-label">{{ 'spaceDetails.legacyId' | translate }}</div>
                            <div class="kv-value">{{ parkingSpace()?.oldParkingSiteId ?? 'N/A' }}</div>
                        </div>
                    </div>

                    @if (parkingSpace()?.description) {
                    <p class="text-sm text-slate-500 mt-4 pt-4 border-t border-slate-100">
                        {{ parkingSpace()?.description }}
                    </p>
                    }
                </section>

                <!-- Card B: Location Information (span 1) -->
                <section class="detail-card bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                    <div class="flex items-center gap-2 mb-4">
                        <mat-icon class="section-icon !w-5 !h-5 !text-[20px] leading-5">location_on</mat-icon>
                        <h2 class="text-sm font-semibold text-slate-900">{{ 'spaceDetails.location' | translate }}</h2>
                    </div>

                    <div class="flex items-start justify-between gap-3">
                        <div class="text-sm space-y-2 min-w-0">
                            @if (parkingSpace()?.address; as address) {
                            <div>
                                <div class="kv-label">{{ 'spaceDetails.address' | translate }}</div>
                                <div class="kv-value truncate">{{ address.firstAddressLine || '—' }}</div>
                            </div>
                            <div>
                                <div class="kv-label">{{ 'spaceDetails.city' | translate }}</div>
                                <div class="kv-value truncate">{{ address.locality || '—' }}</div>
                            </div>
                            <div class="flex gap-6">
                                <div>
                                    <div class="kv-label">{{ 'spaceDetails.postalCode' | translate }}</div>
                                    <div class="kv-value">{{ address.postCode || '—' }}</div>
                                </div>
                                <div>
                                    <div class="kv-label">{{ 'spaceDetails.country' | translate }}</div>
                                    <div class="kv-value">{{ address.countryCode || '—' }}</div>
                                </div>
                            </div>
                            } @else {
                            <p class="text-slate-400">{{ 'spaceDetails.locationNotSet' | translate }}</p>
                            }
                        </div>

                        <!-- Inline map thumbnail -->
                        <div class="map-thumb shrink-0">
                            <div class="map-thumb__grid"></div>
                            <mat-icon class="map-thumb__marker">place</mat-icon>
                            @if (hasCoordinates()) {
                            <span class="map-thumb__coords">
                                {{ parkingSpace()?.address?.latitude | number:'1.3-3' }},
                                {{ parkingSpace()?.address?.longitude | number:'1.3-3' }}
                            </span>
                            }
                        </div>
                    </div>
                </section>
            </div>

            <!-- ===== 3 + 4. Hardware/Devices + Parking Zones ===== -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

                <!-- Infrastructure & Devices (Access Groups removed) -->
                @if (isAdmin()) {
                <section class="detail-card bg-white border border-slate-100 rounded-xl shadow-sm p-4">
                    <div class="flex items-center justify-between gap-2 mb-3">
                        <div class="flex items-center gap-2 min-w-0">
                            <mat-icon class="section-icon !w-5 !h-5 !text-[20px] leading-5">developer_board</mat-icon>
                            <h2 class="text-sm font-semibold text-slate-900">{{ 'spaceDetails.infrastructure' |
                                translate }}</h2>
                        </div>
                        @if (infrastructure()) {
                        <button type="button" (click)="configureInfrastructure()"
                            class="text-xs bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-lg font-medium transition-colors">
                            {{ 'spaceDetails.configureGroups' | translate }}
                        </button>
                        }
                    </div>

                    @if (infrastructure()) {
                    <!-- Telemetry summary -->
                    <div class="grid grid-cols-2 gap-2 mb-3">
                        <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                            <div class="text-lg font-bold text-slate-900 leading-tight">{{ deviceGroupCount() }}</div>
                            <div class="text-xs text-slate-500">{{ 'spaceDetails.deviceGroups' | translate }}</div>
                        </div>
                        <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                            <div class="text-lg font-bold text-slate-900 leading-tight">{{ totalCameras() }}</div>
                            <div class="text-xs text-slate-500">{{ 'spaceDetails.cameras' | translate }}</div>
                        </div>
                    </div>

                    <!-- Device group rows (no inline configure actions) -->
                    @if (infrastructure()?.deviceGroups && infrastructure()!.deviceGroups!.length > 0) {
                    <div class="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
                        @for (deviceGroup of infrastructure()!.deviceGroups!; track deviceGroup.id) {
                        <div class="flex items-center justify-between gap-3 px-3 py-2">
                            <div class="flex items-center gap-2.5 min-w-0">
                                <div class="device-dot"><mat-icon
                                        class="!w-4 !h-4 !text-[16px] leading-4">memory</mat-icon></div>
                                <div class="min-w-0">
                                    <p class="text-sm font-medium text-slate-900 truncate">{{ deviceGroup.groupName }}
                                    </p>
                                    <p class="text-xs text-slate-500 truncate">{{ deviceGroup.groupType }} &middot; {{
                                        deviceGroup.cameras?.length || 0 }} {{ 'spaceDetails.camerasSuffix' | translate
                                        }}</p>
                                </div>
                            </div>
                            <span class="status-pill status-pill--ok shrink-0">
                                <span class="status-dot is-active"></span> {{ 'common.active' | translate }}
                            </span>
                        </div>
                        }
                    </div>
                    } @else {
                    <div class="text-center text-sm text-slate-500 bg-slate-50 rounded-lg py-4">{{
                        'spaceDetails.noDeviceGroups' | translate }}</div>
                    }

                    <!-- Master Controllers action row -->
                    <div class="mt-3 pt-3 border-t border-slate-100 space-y-2">
                        <div class="flex items-center justify-between gap-3">
                            <div class="flex items-center gap-2.5 min-w-0">
                                <div class="device-dot device-dot--ctl"><mat-icon
                                        class="!w-4 !h-4 !text-[16px] leading-4">router</mat-icon></div>
                                <div class="min-w-0">
                                    <p class="text-sm font-medium text-slate-900">{{ 'spaceDetails.masterControllers' |
                                        translate }}</p>
                                    <p class="text-xs text-slate-500">{{ 'spaceDetails.iotHubMapping' | translate }}</p>
                                </div>
                            </div>
                            <button type="button" (click)="configureMasterControllers()"
                                class="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-colors shrink-0">
                                {{ 'spaceDetails.manage' | translate }}
                            </button>
                        </div>
                    </div>
                    } @else {
                    <div class="text-center py-8">
                        <mat-icon class="text-slate-300"
                            style="font-size:40px;width:40px;height:40px;">developer_board</mat-icon>
                        <p class="text-sm text-slate-600 mt-2">{{ 'spaceDetails.noInfrastructure' | translate }}</p>
                        <button type="button" (click)="addInfrastructure()" [disabled]="isAddingInfrastructure()"
                            class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-colors">
                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">add</mat-icon>
                            {{ 'spaceDetails.addInfrastructure' | translate }}
                        </button>
                    </div>
                    }
                </section>
                }

                <!-- Parking Zones & live occupancy -->
                <section class="detail-card bg-white border border-slate-100 rounded-xl shadow-sm p-4"
                    [class.lg:col-span-2]="!isAdmin()">
                    <div class="flex items-center gap-2 mb-3">
                        <mat-icon class="section-icon !w-5 !h-5 !text-[20px] leading-5">grid_view</mat-icon>
                        <h2 class="text-sm font-semibold text-slate-900">{{ 'spaceDetails.zonesOccupancy' | translate }}
                        </h2>
                    </div>

                    <!-- Live stats -->
                    <div class="grid grid-cols-3 gap-2 mb-3">
                        <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                            <div class="text-xs text-slate-500">{{ 'spaceDetails.capacity' | translate }}</div>
                            <div class="text-lg font-bold text-slate-900 leading-tight">
                                {{ totalCapacity() > 0 ? totalCapacity() : '—' }}
                            </div>
                        </div>
                        <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                            <div class="text-xs text-slate-500">{{ 'spaceDetails.occupied' | translate }}</div>
                            <div class="text-lg font-bold text-slate-900 leading-tight">
                                @if (isLoadingSummary()) { … } @else { {{ occupiedCount() }} }
                            </div>
                        </div>
                        <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                            <div class="text-xs text-slate-500">{{ 'spaceDetails.available' | translate }}</div>
                            <div class="text-lg font-bold leading-tight" [class]="availabilityColorClass()">
                                @if (isLoadingSummary() || availabilityPercent() === null) { — } @else { {{
                                availabilityPercent() }}% }
                            </div>
                        </div>
                    </div>

                    <!-- Zone selector -->
                    @if (parkingSpace()?.parkingZones && parkingSpace()!.parkingZones!.length > 0) {
                    <div>
                        <label class="kv-label block mb-1" for="zoneSelect">{{ 'spaceDetails.linkedZone' | translate
                            }}</label>
                        <select id="zoneSelect" class="zone-select"
                            (change)="selectedZoneId.set($any($event.target).value)">
                            @for (zone of parkingSpace()!.parkingZones!; track zone.id) {
                            <option [value]="zone.id" [selected]="zone.id === selectedZone().id">{{ zone.name }}
                            </option>
                            }
                        </select>
                        <div class="flex items-center gap-2 mt-2 flex-wrap">
                            <span class="zone-chip">
                                <mat-icon class="!w-4 !h-4 !text-[14px] leading-4">place</mat-icon>
                                {{ selectedZone().name }}
                            </span>
                            @if (selectedZone().capacity) {
                            <span class="text-xs text-slate-500">{{ 'spaceDetails.capacity' | translate }} <strong
                                    class="text-slate-700">{{ selectedZone().capacity }}</strong></span>
                            }
                            <span class="text-xs text-slate-500">{{ 'spaceDetails.occupied' | translate }} <strong
                                    class="text-slate-700">{{ selectedZoneSummary()?.activeProcessCount ?? 0
                                    }}</strong></span>
                        </div>
                        @if (selectedZone().description) {
                        <p class="text-xs text-slate-500 mt-2">{{ selectedZone().description }}</p>
                        }
                    </div>
                    } @else {
                    <div class="text-center text-sm text-slate-500 bg-slate-50 rounded-lg py-4">{{
                        'spaceDetails.noZonesLinked' | translate }}</div>
                    }
                </section>
            </div>

            <!-- ===== Parking Types ===== -->
            <app-parking-type-space [parkingSpace]="parkingSpace()!"
                [parkingOperatorId]="parkingSpace()?.createdById ?? undefined" class="block">
            </app-parking-type-space>

            <!-- ===== Easypass features — switched on per customer by JJames, Administrator only ===== -->
            @if (isAdmin()) {
            <section class="detail-card bg-white border border-slate-100 rounded-xl shadow-sm p-4">
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                        <mat-icon class="section-icon !w-5 !h-5 !text-[20px] leading-5">qr_code_2</mat-icon>
                        <h2 class="text-sm font-semibold text-slate-900">{{ 'easypassModes.sectionTitle' | translate }}</h2>
                    </div>
                    <button mat-stroked-button class="!text-xs" (click)="editEasypassModes()">
                        <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">edit</mat-icon>
                        {{ 'latePayment.configure' | translate }}
                    </button>
                </div>
                <div class="flex flex-wrap gap-2 text-sm">
                    <span class="attr-pill" [class.attr-pill--green]="parkingSpace()?.easypassSimpleEnabled">{{ 'easypassModes.simple' | translate }}</span>
                    <span class="attr-pill" [class.attr-pill--green]="parkingSpace()?.easypassMultiQrEnabled">{{ 'easypassModes.multiQr' | translate }}</span>
                    <span class="attr-pill" [class.attr-pill--green]="parkingSpace()?.easypassAdvancedEnabled">{{ 'easypassModes.advanced' | translate }}</span>
                </div>
            </section>

            <!-- ===== Quick Easypass (Hummelhofbad) — Administrator only, like the modes above ===== -->
            <section class="detail-card bg-white border border-slate-100 rounded-xl shadow-sm p-4">
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                        <mat-icon class="section-icon !w-5 !h-5 !text-[20px] leading-5">confirmation_number</mat-icon>
                        <h2 class="text-sm font-semibold text-slate-900">{{ 'quickEasypass.sectionTitle' | translate }}</h2>
                        @if (parkingSpace()?.quickEasypassEnabled) {
                        <span class="attr-pill attr-pill--green">{{ 'latePayment.on' | translate }}</span>
                        } @else {
                        <span class="attr-pill">{{ 'latePayment.off' | translate }}</span>
                        }
                    </div>
                    <button mat-stroked-button class="!text-xs" (click)="editQuickEasypassSettings()">
                        <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">edit</mat-icon>
                        {{ 'latePayment.configure' | translate }}
                    </button>
                </div>

                @if (parkingSpace()?.quickEasypassEnabled) {
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4 text-sm">
                    <div>
                        <div class="kv-label">{{ 'quickEasypass.cutoff' | translate }}</div>
                        <div class="kv-value">{{ (parkingSpace()?.quickEasypassCutoffLocalTime ?? '').slice(0, 5) }}</div>
                    </div>
                    <div>
                        <div class="kv-label">{{ 'quickEasypass.chargeAfterCutoff' | translate }}</div>
                        <div class="kv-value">
                            {{ (parkingSpace()?.quickEasypassChargeAfterCutoff ? 'quickEasypass.yes' : 'quickEasypass.no') | translate }}
                        </div>
                    </div>
                </div>
                }
            </section>
            }

            <!-- ===== Late payment (KAN-458 D-37) — Administrator only ===== -->
            @if (isAdmin()) {
            <section class="detail-card bg-white border border-slate-100 rounded-xl shadow-sm p-4">
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                        <mat-icon class="section-icon !w-5 !h-5 !text-[20px] leading-5">schedule_send</mat-icon>
                        <h2 class="text-sm font-semibold text-slate-900">{{ 'latePayment.sectionTitle' | translate }}
                        </h2>
                        @if (parkingSpace()?.latePaymentEnabled) {
                        <span class="attr-pill attr-pill--green">{{ 'latePayment.on' | translate }}</span>
                        } @else {
                        <span class="attr-pill">{{ 'latePayment.off' | translate }}</span>
                        }
                    </div>
                    <button mat-stroked-button class="!text-xs" (click)="editLatePaymentSettings()">
                        <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">edit</mat-icon>
                        {{ 'latePayment.configure' | translate }}
                    </button>
                </div>

                @if (parkingSpace()?.latePaymentEnabled) {
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4 text-sm">
                    <div>
                        <div class="kv-label">{{ 'latePayment.windowHours' | translate }}</div>
                        <div class="kv-value">{{ parkingSpace()?.latePaymentWindowHours }} h</div>
                    </div>
                    <div>
                        <div class="kv-label">{{ 'latePayment.maxAmount' | translate }}</div>
                        <div class="kv-value">
                            {{ parkingSpace()?.latePaymentMaxAmount ?? ('latePayment.maxAmountUncapped' | translate) }}
                        </div>
                    </div>
                    <div>
                        <div class="kv-label">{{ 'latePayment.abandonedThreshold' | translate }}</div>
                        <div class="kv-value">
                            @if (parkingSpace()?.abandonedStayThresholdHours; as threshold) {
                            {{ threshold }} h
                            } @else {
                            {{ 'latePayment.abandonedThresholdNone' | translate }}
                            }
                        </div>
                    </div>
                </div>
                } @else {
                <p class="text-sm text-slate-500">{{ 'latePayment.disabledExplainer' | translate }}</p>
                }
            </section>
            }


        </div>

        }
    </div>
</div>